import React, { useEffect, useRef, useState } from "react"

import { DashboardEmbedType } from "@common-shared/config/metabase.config"
import { ITokenResponseData } from "@common-shared/types/responseData/commonResponseData.types"

import { SpinnerElement } from "@front-shared/elements/spinner.element"

import { MetabaseHelper } from "../../helpers/metabase.helper"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { getDashboardEmbedToken } from "../../store/thunks/dashboard.thunks"

export const DashboardEmbedComponent: React.FunctionComponent<
    IDashboardEmbedComponentProps
> = props => {
    const containerRef = useRef<HTMLDivElement>(null)

    const dispatch = useAppDispatch()
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [embedToken, setEmbedToken] = useState<string | null>(null)
    const [scriptReady, setScriptReady] = useState<boolean>(false)

    const isReady = scriptReady && embedToken !== null

    // Load embed.js once
    useEffect(() => {
        MetabaseHelper.loadMetabaseEmbedScript()
            .then(() => {
                setScriptReady(true)
            })
            .catch(err => {
                console.error("Metabase embed.js load failed:", err)
            })
    }, [])

    // Fetch the per-resource JWT
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        // Reset token so we don't render with a stale one while a new one is fetched
        setEmbedToken(null)

        dispatch(
            getDashboardEmbedToken({
                practice: selectedPracticeRole.practice,
                payload: {
                    embedType: props.embedType,
                    embedId: props.embedId,
                },
                onSuccess: (result: ITokenResponseData) => {
                    setEmbedToken(result.token)
                },
            }),
        )
    }, [selectedPracticeRole?.id, props.embedType, props.embedId])

    const renderEmbed = (): React.ReactNode => {
        if (!isReady) {
            return <SpinnerElement containerClassName="text-center mt-[48px]" />
        }

        // key forces remount when the token changes, so the
        // web component picks up the new token cleanly
        switch (props.embedType) {
            case DashboardEmbedType.DASHBOARD: {
                return (
                    <metabase-dashboard
                        key={embedToken}
                        token={embedToken}
                        with-title="false"
                        with-downloads="false"
                    />
                )
            }

            case DashboardEmbedType.QUESTION: {
                return (
                    <metabase-question
                        key={embedToken}
                        token={embedToken}
                        with-title="false"
                        with-downloads="false"
                    />
                )
            }

            default: {
                return null
            }
        }
    }

    return (
        <div
            ref={containerRef}
            className={`relative ${props.className ?? ""}`}
            style={props.style}
        >
            {renderEmbed()}
        </div>
    )
}

interface IDashboardEmbedComponentProps {
    className?: string
    style?: React.CSSProperties
    embedType: DashboardEmbedType
    embedId: number
}
